import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ──────────────────────────────────────────────────────────────────
export interface Subcategory {
  id: string;
  category_id: string;
  slug: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  aliases: string[];
    image_url: string | null;
    image_alt: string | null;
}

export interface DynamicCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon_name: string | null;
  display_order: number;
  show_in_hero: boolean;
  show_in_popular: boolean;
  requires_certifications: boolean;
  /** Logical group used by GroupedSelect — admin-editable via Category Management */
  category_group: string;
  image_url: string | null;
  image_alt: string | null;
  /**
   * Historical spellings this category is also known by ("Cleaning",
   * "Housekeeping" for "House Cleaning"), seeded in service_categories.
   * Read from the base table because v_active_categories_with_image does not
   * expose the column — so a rebook's category fallback, which resolves by
   * name, could never match one (Codex, PR #996). Absent on the offline list.
   */
  aliases?: string[] | null;
  subcategories: Subcategory[];
  /**
   * Bookings placed in this category, folded across free-text spellings.
   * Optional: absent on the offline fallback list, which has no counts to
   * report. Consumers treat absent as zero rather than as "unknown".
   */
  booking_count?: number;
  /** Distinct helpers currently offering it. Optional, as above. */
  helper_count?: number;
  /**
   * Real homepage stats from v_category_stats. Nullable/zero where there is no
   * data (no available offering, no verified reviews) — consumers must omit the
   * stat rather than show a placeholder, which is the whole point of this data
   * replacing Index.tsx's fabricated fixed values.
   */
  min_rate?: number | null;
  avg_rating?: number | null;
  review_count?: number;
}

/** Known category groups (kept in sync with DB seeds). Used as fallback ordering. */
export const CATEGORY_GROUP_ORDER: readonly string[] = [
  "Home Services",
  "Care & Support",
  "Trades & Technical",
  "Lifestyle & Beauty",
  "Education & Skills",
  "Moving & Transport",
  "Other",
] as const;

// ── Hardcoded fallback (used if DB is unavailable) ─────────────────────────
/** The query's own answer about whether it could read the whole catalogue. */
interface CatalogueResult {
  categories: DynamicCategory[];
  /**
   * Every category carries its real children. False for the offline list and
   * for a category read that succeeded while the subcategory read failed —
   * both leave children empty, and only the query knows which happened.
   */
  complete: boolean;
  /**
   * Every category carries the spellings it is also known by. False when the
   * alias read failed, which leaves the column indistinguishable from a
   * category that genuinely has no aliases — and a resolver that cannot see a
   * category's alias will look for the text among CHILDREN instead.
   */
  aliasesKnown: boolean;
}

/**
 * A read that did not bring back everything the resolvers rely on — either the
 * children or the aliases. Both are refetched promptly rather than cached for
 * the usual five minutes: each one silently narrows what a rebook can resolve,
 * and the backend usually recovers long before that (Codex, PR #996).
 */
const isPartialRead = (result: CatalogueResult | undefined): boolean =>
  result?.complete === false || result?.aliasesKnown === false;

export const FALLBACK_CATEGORIES: DynamicCategory[] = [
  { id: "house-cleaning", slug: "house-cleaning", name: "House Cleaning", category_group: "Home Services", description: "Home cleaning, deep cleaning, post-construction.", icon_name: "Sparkles", display_order: 10, show_in_hero: true, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "childcare", slug: "childcare", name: "Childcare", category_group: "Care & Support", description: "Babysitting, nannying, school runs.", icon_name: "Baby", display_order: 20, show_in_hero: true, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "plumbing", slug: "plumbing", name: "Plumbing", category_group: "Trades & Technical", description: "Leaks, installations, repairs.", icon_name: "Wrench", display_order: 30, show_in_hero: true, show_in_popular: true, requires_certifications: true, image_url: null, image_alt: null, subcategories: [] },
  { id: "tutoring", slug: "tutoring", name: "Tutoring", category_group: "Education & Skills", description: "Academic tutoring across all levels.", icon_name: "GraduationCap", display_order: 40, show_in_hero: true, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "electrical", slug: "electrical", name: "Electrical", category_group: "Trades & Technical", description: "Wiring, repairs, installations.", icon_name: "Zap", display_order: 50, show_in_hero: true, show_in_popular: true, requires_certifications: true, image_url: null, image_alt: null, subcategories: [] },
  { id: "elderly-care", slug: "elderly-care", name: "Elderly Care", category_group: "Care & Support", description: "Companion care and support for older adults.", icon_name: "Heart", display_order: 60, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "cooking", slug: "cooking", name: "Cooking", category_group: "Home Services", description: "Personal chefs, meal prep, catering.", icon_name: "ChefHat", display_order: 70, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "gardening", slug: "gardening", name: "Gardening", category_group: "Home Services", description: "Lawn care, landscaping, garden maintenance.", icon_name: "Sprout", display_order: 80, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "laundry", slug: "laundry", name: "Laundry", category_group: "Home Services", description: "Laundry pickup, washing, ironing.", icon_name: "Shirt", display_order: 90, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "pet-care", slug: "pet-care", name: "Pet Care", category_group: "Care & Support", description: "Dog walking, pet sitting, grooming.", icon_name: "Dog", display_order: 100, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "moving", slug: "moving", name: "Moving", category_group: "Moving & Transport", description: "House moving and relocation help.", icon_name: "Truck", display_order: 110, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "handyman", slug: "handyman", name: "Handyman", category_group: "Home Services", description: "General home repairs and small jobs.", icon_name: "Hammer", display_order: 120, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "beauty", slug: "beauty", name: "Beauty", category_group: "Lifestyle & Beauty", description: "Hair, makeup, beauty at home.", icon_name: "Scissors", display_order: 130, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "car-wash", slug: "car-wash", name: "Car Wash", category_group: "Lifestyle & Beauty", description: "Mobile car wash and detailing.", icon_name: "Car", display_order: 140, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "event-help", slug: "event-help", name: "Event Help", category_group: "Lifestyle & Beauty", description: "Event staff and party support.", icon_name: "PartyPopper", display_order: 150, show_in_hero: false, show_in_popular: true, requires_certifications: false, image_url: null, image_alt: null, subcategories: [] },
  { id: "security", slug: "security", name: "Security", category_group: "Trades & Technical", description: "Personal and property security.", icon_name: "Shield", display_order: 160, show_in_hero: false, show_in_popular: true, requires_certifications: true, image_url: null, image_alt: null, subcategories: [] },
];

// ── Main hook ──────────────────────────────────────────────────────────────
/**
 * Fetches active service categories from v_active_categories_with_image,
 * plus all active subcategories joined in.
 * Falls back to FALLBACK_CATEGORIES if the view is unavailable.
 */
export function useDynamicCategories() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dynamic-categories"],
    queryFn: async (): Promise<CatalogueResult> => {
      // Fetch categories
      const { data: catData, error: catError } = await supabase
        .from("v_active_categories_with_image")
        .select(
          "id, slug, name, description, icon_name, display_order, show_in_hero, " +
          "show_in_popular, requires_certifications, category_group, image_url, image_alt"
        )
        .order("display_order", { ascending: true });

      if (catError) {
        console.warn("[useDynamicCategories] DB unavailable, using fallback:", catError.message);
        return { categories: FALLBACK_CATEGORIES, complete: false, aliasesKnown: false };
      }
      if (!catData || catData.length === 0) {
        return { categories: FALLBACK_CATEGORIES, complete: false, aliasesKnown: false };
      }

      /*
        The aliases the category is ALSO known by. Not in the view at all, so
        `resolveCategoryOrSubcategory` — which honours them when supplied —
        had nothing to match against, and a legacy booking whose
        `service_category` reads "Cleaning" rather than "House Cleaning"
        resolved to nothing: 24 of staging's 104 completed bookings (Codex,
        PR #996). The base table carries a public read policy for active rows.

        Non-fatal, unlike the subcategory read below: a category recovered by
        name or slug is unaffected, and that is 52 of the 89 staging rebooks
        this fallback rescues. But it is REPORTED, because "no aliases" and
        "aliases unread" are the same shape and they are not the same fact.

        `resolveStoredCategory` tries a category's alias BEFORE a child's
        parent, so erasing the aliases promotes that last tier: Beauty's alias
        "Grooming" disappears, Pet Care's "Grooming" child is then the only
        answer, and the rebook takes that child's parent — and, unbounded, the
        child's id and its price. The tier is disabled while this is false
        rather than the whole fallback, which would cost those 52 recoveries
        for a failure that does not touch them (Codex, PR #996).
      */
      const { data: aliasRows, error: aliasError } = await supabase
        .from("service_categories")
        .select("id, aliases")
        .eq("is_active", true);
      if (aliasError) {
        console.warn("[useDynamicCategories] category aliases unavailable:", aliasError.message);
      }
      const aliasesById: Record<string, string[]> = {};
      for (const row of aliasRows ?? []) aliasesById[row.id] = row.aliases ?? [];

      // Fetch all active subcategories in one query
      const { data: subData, error: subError } = await supabase
        .from("service_subcategories")
        .select("id, category_id, slug, name, description, display_order, is_active, aliases, image_id")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

          const imageIds = Array.from(new Set<string>(
            (subData ?? [])
              .map((s: { image_id?: string | null }) => s.image_id)
              .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)
          ));
    const subImageMap: Record<string, { url: string; alt: string | null }> = {};
    if (imageIds.length > 0) {
      const { data: imgRows } = await supabase
        .from("app_images")
        .select("id, public_url, alt_text")
        .in("id", imageIds);
      for (const img of (imgRows ?? [])) {
        subImageMap[img.id] = { url: img.public_url, alt: img.alt_text };
      }
    }

    const subcatMap: Record<string, Subcategory[]> = {};
    for (const sub of (subData ?? [])) {
      if (!subcatMap[sub.category_id]) subcatMap[sub.category_id] = [];
      const img = sub.image_id ? subImageMap[sub.image_id] : undefined;
      subcatMap[sub.category_id].push({
        ...sub,
        image_url: img ? img.url : null,
        image_alt: img ? img.alt : null,
      } as Subcategory);
    }

      // Measured popularity, so "popular services" reflects what people
      // actually book rather than a hand-set order. Aggregate counts only.
      // Failure is non-fatal: without it the lists simply keep display_order.
      // types.ts predates this view, and must stay a union of both projects'
      // schemas — regenerating against one breaks the other's pages. Cast the
      // client for this read rather than regenerate.
      type PopularityRow = { category_id: string; booking_count: number; helper_count: number };
      const popClient = supabase as unknown as {
        from: (t: 'v_category_popularity') => {
          select: (cols: string) => Promise<{ data: PopularityRow[] | null }>;
        };
      };
      const { data: popRows } = await popClient
        .from("v_category_popularity")
        .select("category_id, booking_count, helper_count");

      const popularity: Record<string, { bookings: number; helpers: number }> = {};
      for (const r of (popRows ?? [])) {
        popularity[r.category_id] = { bookings: r.booking_count ?? 0, helpers: r.helper_count ?? 0 };
      }

      // Real per-category stats (v_category_stats) — the honest replacement for
      // Index.tsx's fabricated fixed price/rating/count line. Same cast
      // pattern as popularity above (view predates types.ts). A failure here
      // leaves the stats undefined and the card simply omits them.
      type StatsRow = {
        category_id: string; min_rate: number | null;
        helper_count: number | null; avg_rating: number | null; review_count: number | null;
      };
      const statsClient = supabase as unknown as {
        from: (t: 'v_category_stats') => {
          select: (cols: string) => Promise<{ data: StatsRow[] | null }>;
        };
      };
      const { data: statRows } = await statsClient
        .from("v_category_stats")
        .select("category_id, min_rate, helper_count, avg_rating, review_count");
      const stats: Record<string, Omit<StatsRow, "category_id">> = {};
      for (const r of (statRows ?? [])) {
        stats[r.category_id] = {
          min_rate: r.min_rate, helper_count: r.helper_count,
          avg_rating: r.avg_rating, review_count: r.review_count,
        };
      }

      // supabase-js mistypes this view's select result; cast to the known row shape
      const catRows = catData as unknown as Omit<DynamicCategory, "subcategories">[];
      /*
        A failed subcategory read is NOT an empty catalogue. Supabase resolves a
        failure with `{ data: null, error }`, and this destructured only `data`,
        so every category came back carrying `subcategories: []` — real rows,
        indistinguishable from categories that genuinely have no children, and
        not reference-equal to FALLBACK_CATEGORIES either (Codex, PR #996).
        Popularity and stats stay non-fatal: they only decorate.
      */
      const complete = !subError;
      const aliasesKnown = !aliasError;
      if (subError) {
        console.warn("[useDynamicCategories] subcategories unavailable:", subError.message);
      }
      return { complete, aliasesKnown, categories: catRows.map((cat) => ({
        ...cat,
        category_group: cat.category_group ?? "Other",
        aliases: aliasesById[cat.id] ?? [],
        subcategories: subcatMap[cat.id] ?? [],
        booking_count: popularity[cat.id]?.bookings ?? 0,
        // Prefer the stats view's live helper count; fall back to popularity's.
        helper_count: stats[cat.id]?.helper_count ?? popularity[cat.id]?.helpers ?? 0,
        min_rate: stats[cat.id]?.min_rate ?? null,
        avg_rating: stats[cat.id]?.avg_rating ?? null,
        review_count: stats[cat.id]?.review_count ?? 0,
      })) };
    },
    /*
      An INCOMPLETE result is never fresh. It resolves as a success — that is
      the point of the `complete` flag — so without this react-query would hold
      it for the full five minutes with `retry: false`, and a client sitting on
      step 1 stays blocked long after the backend recovers. Incomplete reads go
      stale at once and retry on their own every half minute; the panel also
      offers a retry, because nobody should have to wait for a timer.
      (Codex, PR #996.)
    */
    staleTime: (query) => (isPartialRead(query.state.data) ? 0 : 5 * 60 * 1000),
    refetchInterval: (query) => (isPartialRead(query.state.data) ? 30_000 : false),
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  const categories = data?.categories ?? FALLBACK_CATEGORIES;
  /*
    Whether the children on these categories can be trusted.

    Carried out of the query as a flag rather than inferred, because neither
    `error` nor identity can tell. The query RETURNS the offline list when the
    view is unavailable, so react-query reports success and `error` is null;
    and react-query's structural sharing may hand back a newly constructed
    array on a refetch, so `data === FALLBACK_CATEGORIES` is false even when
    the contents are exactly that. A third path has no identity to compare at
    all: real categories whose subcategory read failed (Codex, PR #996).

    It matters because a caller that reads an empty children array as "this
    category is already fully specific" would let a booking through with no
    subcategory id, which is what prices it.
  */
  const isFallbackCatalogue = !data || !data.complete;
  /*
    Separate from `isFallbackCatalogue` on purpose: an unread alias column is
    not an unread catalogue, and blocking every rebook over it would trade 52
    working recoveries against one narrow misresolution. It disables exactly
    the resolver tier that the missing column makes unsafe.
  */
  const aliasesKnown = !!data && data.aliasesKnown;

  /**
   * Same categories, ranked by what people actually book.
   *
   * Sorted defensively rather than relying on query order: the view is fetched
   * separately from the categories, and the fallback list carries zeroes.
   */
  const byPopularity = [...categories].sort(
    (a, b) =>
      (b.booking_count ?? 0) - (a.booking_count ?? 0) ||
      (b.helper_count ?? 0) - (a.helper_count ?? 0) ||
      a.display_order - b.display_order,
  );

  /** Unique category groups, ordered by CATEGORY_GROUP_ORDER then alphabetically */
  const categoryGroups: string[] = Array.from(new Set(categories.map((c) => c.category_group))).sort(
    (a, b) => {
      const ai = CATEGORY_GROUP_ORDER.indexOf(a);
      const bi = CATEGORY_GROUP_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    }
  );

  return {
    categories,
    categoryGroups,
    /**
     * Every ACTIVE category, ordered by MEASURED popularity.
     *
     * These used to gate on `show_in_hero` / `show_in_popular`, so the homepage
     * and footer advertised a subset chosen by two booleans while the bookable
     * catalogue was something else entirely.
     *
     * Now the catalogue decides what exists (activate a category and it
     * appears; deactivate it and it disappears everywhere) and the data decides
     * what leads: demand first, then supply, then the admin's `display_order`
     * as a deterministic tie-break so ordering stays stable while counts are
     * still small.
     *
     * Bookings are counted on the RESOLVED category — `service_category` is
     * free text, so raw counts split "childcare" (24) from "Childcare" (15)
     * and buried the real leader.
     */
    heroCategories: byPopularity,
    popularCategories: byPopularity,
    allNames: categories.map((c) => c.name),
    isLoading,
    error,
    /** True while `categories` is the offline list rather than the catalogue. */
    isFallbackCatalogue,
    /**
     * False when the category alias column could not be read, so a resolver
     * must not infer a category from its children — it cannot see the aliases
     * that would have outranked them.
     */
    aliasesKnown,
    /** Read the catalogue again now, rather than waiting for the retry timer. */
    refetchCatalogue: () => { void refetch(); },
  };
      }

// ── Category-by-slug (bypasses the Active/Inactive toggle) ────────────────
/** Minimal subcategory shape returned by get_category_with_subcategories. */
export interface CategorySubcategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  display_order: number;
  image_url: string | null;
  image_alt: string | null;
}

export interface CategoryWithSubcategories {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon_name: string | null;
  image_url: string | null;
  image_alt: string | null;
  subcategories: CategorySubcategory[];
}

/**
 * Fetches a single category by slug PLUS its real admin-configured
 * subcategories/images via the get_category_with_subcategories RPC —
 * regardless of the category's Active/Inactive toggle in Category
 * Management. That toggle only controls whether a category is included in
 * browse/homepage aggregation (see useDynamicCategories above); the
 * dedicated per-service landing pages (e.g. /elderly-care, /pet-care) stay
 * linked from navigation even when a category is inactive, so they should
 * still show its real subcategories/images instead of silently falling back
 * to the generic hardcoded service-type list.
 */
export function useCategoryBySlug(slug: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["category-by-slug", slug],
    queryFn: async (): Promise<CategoryWithSubcategories | null> => {
      const { data, error } = await supabase.rpc(
        "get_category_with_subcategories",
        { p_slug: slug }
      );
      if (error || !data || data.length === 0) return null;
      const row = data[0] as any;
      return {
        id: row.id,
        slug: row.slug,
        name: row.name,
        description: row.description,
        icon_name: row.icon_name,
        image_url: row.image_url,
        image_alt: row.image_alt,
        subcategories: (row.subcategories ?? []) as CategorySubcategory[],
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  return { category: data ?? null, isLoading, error };
}
